// Simpel Pinia store med en tæller som state
import { createPinia, defineStore } from 'pinia';
import { getStorageService } from '@/services/storageFactory';
import type { IContentStorage } from '@/services/contentStorage.interface';

export const useQuickGuideStateStore = defineStore('QuickGuideState', {
  state: () => ({
    error: null,
    step: 0, // initial step is 0
    stepFurthest: 0,
    categories: [] as unknown as Category[],
    common: {
      image: {
        url: '',
        alt: ''
      },
      title: '',
      introText: '',
      categoryLead: '',
      questionsLead: '',
      resultHeader: '',
      resultResponseTitle: '',
      resultResponseLead: '',
      resultExtraCategoriesTitle: '',
      resultExtraCategoriesText: '',
      responseYes: '',
      responseNo: '',
      responseDontKnow: '',
      buttonBack: '',
      buttonNext: '',
      buttonGoToQuestions: '',
      buttonShowResults: '',
      buttonStartHere: '',
      categoryPrefix: '',
      pdfResultTitle: '',
      pdfCategoryPrefix: '',
      pdfQuestionPrefix: '',
      downloadAllCategories: '',
      downloadResultForCategory: '',
      downloadResult: '',
      buildingPdf: '',
      readMore: '',
      hide: '',
      thereAreProblems: '',
      problemsDetected: '',
      question: '',
      chooseAnAnswer: '',
      chooseAnOption: ''
    }
  }),
  getters: {
    getCategories() {
      if (this.step === this.getMaxStep) {
        return this.categories.filter((category: any) => !category.isSelected);
      } else {
        return this.categories;
      }
    },
    getSelectedCategories: state => {
      if (state.categories?.length === 0) {
        return [];
      } else {
        return state.categories.length === 0 ? [] : state.categories.filter((category: any) => category.isSelected);
      }
    },
    getMaxStep() {
      return 2 + this.getSelectedCategories?.length;
    },
    getFeedbackForQuestion: state => (categoryIndex: number, questionIndex: number) =>
      state.categories
        .find((category: any) => category.id === categoryIndex)
        ?.questions[questionIndex].responses.find((response: any) => response.isChecked)?.feedback || '',
    getChoiceForQuestion: state => (categoryIndex: number, questionIndex: number) => {
      const label = state.categories
        .find((category: any) => category.id === categoryIndex)
        ?.questions[questionIndex].responses.find((response: any) => response.isChecked)?.label;

      // Translate the label using the common translations
      const translations: Record<string, string> = {
        Ja: state.common.responseYes,
        Nej: state.common.responseNo,
        'Ved ikke': state.common.responseDontKnow
      };

      return label ? translations[label] || label : '';
    }
  },
  actions: {
    async loadContent() {
      try {
        const storage: IContentStorage = await getStorageService();
        const content = await storage.getAllContent();

        this.common = content.common;
        this.categories = content.categories as unknown as Category[];

        console.log('[QuickGuideState] Content loaded successfully');
      } catch (error) {
        console.error('[QuickGuideState] Failed to load content:', error);
        this.error = error as any;
        throw error;
      }
    },
    selectCategory(id: number) {
      const categoryToChange = this.categories.find((category: any) => category.id === id);
      categoryToChange.isSelected = !categoryToChange.isSelected;
    },
    increaseStep() {
      this.step++;
    },
    decreaseStep() {
      this.step--;
    },
    setStep(step: number) {
      this.step = step;
    },
    setFurthestStep(step: number) {
      this.stepFurthest = step;
    },
    checkOption(categoryIndex: number, questionIndex: number, optionLabel: string) {
      const categoryToChange = this.categories.find((category: any) => category.id === categoryIndex);
      const questionToChange = categoryToChange.questions[questionIndex];
      questionToChange.responses.forEach((response: any, index: number) => {
        response.isChecked = response.label === optionLabel;
      });
    },
    resetState() {
      this.step = 0;
      this.stepFurthest = 0;
      this.categories = this.categories.map((category: any) => {
        category.isSelected = false;
        category.questions = category.questions.map((question: any) => {
          question.responses = question.responses.map((response: any) => {
            response.isChecked = false;
            return response;
          });
          return question;
        });
        return category;
      });
    }
  }
});

const store = createPinia();
export default store;
